import {
    PlayerId,
    usePixiRenderOnChange,
    usePlayerHighlight,
    usePlayers,
} from '@glark-newco/game-library';
import type {Filter} from '@pixi/core';
import {Container, Graphics, Text} from '@pixi/react';
import {Sound} from '@pixi/sound';
import {AdjustmentFilter, GlowFilter, OutlineFilter} from 'pixi-filters';
import {Assets, FederatedMouseEvent, IPointData, Polygon} from 'pixi.js';
import {ReactNode, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {FullBodyAvatar} from './FullBodyAvatar';
import {PlayerRings} from './PlayerRings';
import {PopupData} from './RemoteCellLayout';
import {PLAYER_REMOVED} from '../assetManifest';
import {textStyle} from '../constants';
import {useIsLinkValid} from '../hooks/useIsLinkValid';
import {useIsLinkCreationActive, useSelectedNodes, useSelectNodeForLink} from '../state/networkEditStore';
import {NodeId} from '../state/networkStore';
import {usePlayerAvatar} from '../state/PlayerAvatarSlice';
import {usePlayerObjectives} from '../state/PlayerObjectivesSlice';
import {usePlayerRings} from '../state/PlayerRingsSlice';

interface PlayerSpriteProps {
    playerId: PlayerId;
    nodeId: NodeId;
    position: IPointData;
    setPopupData: (data: PopupData | undefined) => void;
}

const hitAreaCords = [-60, -180, -60, 200, 50, 200, 50, -180];
const monochrome = new AdjustmentFilter({saturation:0, brightness:0.3});
const glow =  new GlowFilter({distance: 20, outerStrength: 5, alpha:1, color:0xfff200});
const glowGreen = new GlowFilter({distance: 20, outerStrength: 5, alpha:1, color:0x199c24});
const glowRed = new GlowFilter({distance: 20, outerStrength: 5, alpha:1, color:0x96060b});
const glowBlue = new GlowFilter({distance: 20, outerStrength: 5, alpha:1, color:0x0d27a8});
const outline =  new OutlineFilter(4);

export function PlayerSprite({playerId, nodeId, position, setPopupData}: PlayerSpriteProps): ReactNode {
    const {localPlayer} = usePlayers();
    const objectives = usePlayerObjectives(playerId);
    const activeRings = usePlayerRings(playerId);
    const [playerHighlight] = usePlayerHighlight();
    const avatar = usePlayerAvatar(playerId);

    const links = useSelectedNodes();
    const isLinkValid = useIsLinkValid(nodeId);
    const [hover, setHover] = useState(false);

    const objectivesComplete = useMemo(() => objectives.every(o => !!o.ringId), [objectives]);
    const previouslyComplete = useRef<boolean>(objectivesComplete);

    const debugHitArea = false;
    const scale = useMemo(() => (position.y + 500) / 1080, [position.y]);

    const updateTooltip = useCallback((event: FederatedMouseEvent | undefined) => {
        if (!event)
            setPopupData(undefined);
        else {
            setPopupData({playerId, x: position.x, y: position.y - 75 - (scale * 75)});
        }
    }, [playerId, position, scale]);

    useEffect(() => {
        if (objectivesComplete && !previouslyComplete.current && activeRings.length == 0) {
            setTimeout(() =>
                void Assets.get<Sound>(PLAYER_REMOVED).play(), 700);
            previouslyComplete.current = true;
        }
    }, [previouslyComplete, activeRings]);


    const isLinkCreationActive = useIsLinkCreationActive();
    const selectNodeForLink = useSelectNodeForLink();

    const filters: Filter[] = useMemo(() => {
        const linkModeHighlight = () => {
            if (!isLinkCreationActive) return [];
            if (hover && isLinkValid) return [glowGreen];
            if (hover && !isLinkValid) return [glowRed];
            if (!hover && links.includes(nodeId)) return [glowBlue];
            if (hover) return [glowGreen];
            return [];
        };
        return [
            ...(objectivesComplete && activeRings.length == 0 ? [monochrome] : []),
            ...(playerId === playerHighlight ? [glow] : []),
            ...(localPlayer.playerId === playerId ? [outline] : []),
            ...(linkModeHighlight()),
        ];
    }, [objectivesComplete, playerHighlight, localPlayer, hover, activeRings, isLinkCreationActive, links, nodeId, isLinkValid]);
    usePixiRenderOnChange([filters]);

    return (
        <Container
            eventMode={'static'}
            onmouseenter={(e) => {
                updateTooltip(e);
                setHover(true);
            }}
            onmouseleave={() => {
                updateTooltip(undefined);
                setHover(false);
            }}
            onclick={() => {
                if (localPlayer.isFacilitator && isLinkCreationActive && isLinkValid) {
                    selectNodeForLink(nodeId);
                }
            }}
            hitArea={new Polygon(hitAreaCords)}
            anchor={.5} x={position.x} y={position.y} scale={scale}
            filters={filters}
        >
            {debugHitArea && <Graphics draw={g => g.clear().lineStyle({width: 2.5, color: 0x000000}).drawPolygon(hitAreaCords)}/>}
            <FullBodyAvatar avatar={avatar} position={{x: 0, y: 0}} scale={1}/>
            <PlayerRings x={scale * 22} y={27 + scale * 7} playerId={playerId} scale={1}/>
            {debugHitArea && <Text text={'' + nodeId} style={textStyle({fill:['#FF0000']})} x={-22} y={-150}/>}
        </Container>
    );
}
